import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, Target, RefreshCw } from "lucide-react";

const TYPE_META = {
  enterprise_upgrade: { label: "Enterprise upgrade", cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  seat_upgrade: { label: "Seat upgrade", cls: "bg-violet-50 text-violet-800 border-violet-200" },
  annual_renewal: { label: "Annual renewal", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  onboarding_call: { label: "Onboarding support", cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

export default function AccountOpportunities() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/account-opportunities");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="account-opportunities-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Scanning for opportunities…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  const rows = (data.opportunities || []).slice(0, 10);

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="account-opportunities">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-indigo-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">Account Opportunities</h3>
          <span className="text-xs text-slate-500">{data.count || 0} ready to action</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="account-opportunities-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="account-opportunities-empty">No commercial triggers firing right now.</div>
      ) : (
        <div className="space-y-1.5" data-testid="account-opportunities-list">
          {rows.map((o) => {
            const meta = TYPE_META[o.opportunityType] || { label: o.opportunityType, cls: "bg-slate-50 text-slate-700 border-slate-200" };
            return (
              <div key={o.organisationId + o.opportunityType} className="p-2.5 rounded-md bg-slate-50 border border-slate-100" data-testid={`opportunity-row-${o.organisationId}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`border text-[10px] px-1.5 py-0 ${meta.cls}`}>{meta.label}</Badge>
                  <span className="text-sm font-medium text-slate-900 truncate flex-1">{o.organisation}</span>
                  <span className="text-[11px] font-mono text-slate-600">{o.usersCount}u · {o.signups30d}s/30d</span>
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5">{(o.reasons || []).join(" · ")}</div>
                {o.cta && <div className="text-[11px] text-indigo-700 mt-0.5 italic">→ {o.cta}</div>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
